import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Upload, Pencil, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { completeOnboardingStep, getOnboardingProgress } from '@/integrations/supabase/onboarding';
import { ONBOARDING_STEPS } from '@/constants/onboarding';

interface EmpresaData {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  cpf_cnpj: string;
  endereco: string;
  logo_url: string;
  responsavel: string;
  tipo_negocio?: string;
  conheceu_por?: string;
}

export function Empresa() {
  const navigate = useNavigate();
  const { empresa } = useAuth();
  const empresaId = empresa?.id;
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<EmpresaData>>({});
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');

  const { data: empresaData, isLoading } = useQuery({
    queryKey: ['empresa', empresaId],
    queryFn: async () => {
      if (!empresaId) return null;

      const { data, error } = await supabase
        .from('empresas')
        .select('*')
        .eq('id', empresaId)
        .single();

      if (error) throw error;
      return data as EmpresaData;
    },
    enabled: !!empresaId
  });

  // Query para verificar progresso do onboarding
  const { data: onboardingProgress } = useQuery({
    queryKey: ['onboarding-progress', empresaId],
    queryFn: () => getOnboardingProgress(empresaId!),
    enabled: !!empresaId,
  });

  React.useEffect(() => {
    if (empresaData) {
      setFormData({
        nome: empresaData.nome,
        email: empresaData.email,
        telefone: empresaData.telefone || '',
        cpf_cnpj: empresaData.cpf_cnpj || '',
        endereco: empresaData.endereco || '',
        responsavel: empresaData.responsavel || '',
        logo_url: empresaData.logo_url || '',
        tipo_negocio: empresaData.tipo_negocio || '',
        conheceu_por: empresaData.conheceu_por || ''
      });
      if (empresaData.logo_url) {
        setLogoPreview(empresaData.logo_url);
      }
    }
  }, [empresaData]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!empresaId) throw new Error('Empresa não encontrada');

      let logoUrl = formData.logo_url;

      // Upload da logo se houver arquivo novo
      if (logoFile) {
        const fileName = empresaId; // Usar apenas o UUID como nome do arquivo
        const { error: uploadError } = await supabase.storage
          .from('logos')
          .upload(fileName, logoFile, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: publicData } = supabase.storage
          .from('logos')
          .getPublicUrl(fileName);

        logoUrl = publicData.publicUrl;
      }

      const { error } = await supabase
        .from('empresas')
        .update({
          nome: formData.nome,
          email: formData.email,
          telefone: formData.telefone,
          cpf_cnpj: formData.cpf_cnpj,
          endereco: formData.endereco,
          responsavel: formData.responsavel,
          logo_url: logoUrl,
          tipo_negocio: formData.tipo_negocio,
          conheceu_por: formData.conheceu_por,
          updated_at: new Date().toISOString()
        })
        .eq('id', empresaId);

      if (error) throw error;

      // Marcar etapa de configuração da empresa como concluída (IMPORTANTE: await)
      if (empresaId) {
        await completeOnboardingStep(empresaId, ONBOARDING_STEPS.COMPANY);
      }

      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empresa', empresaId] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress', empresaId] });
      toast.success('✅ Empresa atualizada com sucesso! Etapa 1 concluída!');
      setIsEditing(false);
      setLogoFile(null);

      // Redirecionar de volta ao dashboard APENAS para usuários em onboarding
      if (onboardingProgress?.shouldShowChecklist) {
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    },
    onError: (error) => {
      console.error(error);
      toast.error('Erro ao atualizar empresa');
    }
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-4xl space-y-6">
      <div className="space-y-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/configuracoes')}
          className="mb-2 text-purple-600 hover:bg-purple-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dados da Empresa</h1>
            <p className="text-gray-600">Gerencie as informações da sua empresa</p>
          </div>
          {onboardingProgress?.steps?.some(
            s => s.step === ONBOARDING_STEPS.COMPANY && s.completed
          ) && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-50 border border-green-200">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="text-sm font-semibold text-green-700">Etapa 1 Concluída!</span>
            </div>
          )}
        </div>
      </div>

      {/* Card informativo de onboarding */}
      {onboardingProgress?.shouldShowChecklist && !onboardingProgress?.steps?.some(
        s => s.step === ONBOARDING_STEPS.COMPANY && s.completed
      ) && (
        <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <span>📋</span> Etapa 1 de 5: Configure sua empresa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-gray-700">
              Preencha os dados abaixo e envie sua logo para personalizarem seus documentos. Ao salvar, a etapa será concluída automaticamente.
            </p>
            <div className="flex gap-2 text-xs text-gray-600 bg-white rounded-lg p-2 border border-purple-100">
              <span>✓</span>
              <span>Após salvar, você voltará automaticamente ao dashboard</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Logo */}
      <Card className="border border-gray-200 hover:border-purple-200 transition-colors shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Logo da Empresa</CardTitle>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
              title="Editar logo"
            >
              <Pencil className="w-5 h-5 text-purple-600" />
            </button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {logoPreview && (
            <div className="flex justify-center">
              <img src={logoPreview} alt="Logo" className="h-32 object-contain" />
            </div>
          )}

          {isEditing && (
            <div>
              <Label htmlFor="logo">Enviar Nova Logo</Label>
              <div className="flex gap-2 mt-2">
                <input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="flex-1"
                />
                {logoFile && <span className="text-sm text-green-600">✓ Arquivo selecionado</span>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Informações Básicas */}
      <Card className="border border-gray-200 hover:border-purple-200 transition-colors shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Informações Básicas</CardTitle>
            <CardDescription>Dados gerais da sua empresa</CardDescription>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Editar informações"
            >
              <Pencil className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Razão Social</Label>
            <Input
              value={formData.nome || ''}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              disabled={!isEditing}
              placeholder="Nome da empresa"
            />
          </div>

          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={!isEditing}
              placeholder="email@empresa.com"
            />
          </div>

          <div>
            <Label>Telefone</Label>
            <Input
              value={formData.telefone || ''}
              onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
              disabled={!isEditing}
              placeholder="(11) 99999-9999"
            />
          </div>

          <div>
            <Label>Responsável</Label>
            <Input
              value={formData.responsavel || ''}
              onChange={(e) => setFormData({ ...formData, responsavel: e.target.value })}
              disabled={!isEditing}
              placeholder="Nome do responsável"
            />
          </div>

          <div>
            <Label>Tipo de Negócio</Label>
            <Select value={formData.tipo_negocio || ''} onValueChange={(value) => isEditing && setFormData({ ...formData, tipo_negocio: value })}>
              <SelectTrigger disabled={!isEditing}>
                <SelectValue placeholder="Selecione o tipo de negócio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="atelie">👗 Ateliê</SelectItem>
                <SelectItem value="confeccao">🏭 Confecção</SelectItem>
                <SelectItem value="grafica">🖨️ Gráfica</SelectItem>
                <SelectItem value="outro">📌 Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Como conheceu o Ateliê Pro</Label>
            <Select value={formData.conheceu_por || ''} onValueChange={(value) => isEditing && setFormData({ ...formData, conheceu_por: value })}>
              <SelectTrigger disabled={!isEditing}>
                <SelectValue placeholder="Selecione uma opção" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="amigos">👥 Indicação de Amigos</SelectItem>
                <SelectItem value="google">🔍 Busca na Internet</SelectItem>
                <SelectItem value="instagram">📸 Instagram</SelectItem>
                <SelectItem value="facebook">📱 Facebook</SelectItem>
                <SelectItem value="youtube">🎬 YouTube</SelectItem>
                <SelectItem value="linkedin">💼 LinkedIn</SelectItem>
                <SelectItem value="indicacao">🎁 Código de Indicação</SelectItem>
                <SelectItem value="outro">📌 Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Endereço */}
      <Card className="border border-gray-200 hover:border-purple-200 transition-colors shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Endereço</CardTitle>
            <CardDescription>Localização da empresa</CardDescription>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Editar endereço"
            >
              <Pencil className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Endereço</Label>
            <Textarea
              value={formData.endereco || ''}
              onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
              disabled={!isEditing}
              placeholder="Rua, número, bairro, cidade, estado"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Dados Fiscais (Somente Leitura) */}
      <Card className="border border-gray-200 hover:border-purple-200 transition-colors shadow-sm">
        <CardHeader>
          <CardTitle>Dados Fiscais</CardTitle>
          <CardDescription>Informações para emissão de notas (não editável)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>CNPJ / CPF</Label>
            <div className="px-3 py-2 bg-gray-100 rounded border border-gray-200 text-gray-700">
              {formData.cpf_cnpj || 'Não informado'}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Botões de Ação */}
      <div className="flex gap-2 justify-end">
        {isEditing ? (
          <>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditing(false);
                setLogoFile(null);
                if (empresaData) {
                  setFormData({
                    nome: empresaData.nome,
                    email: empresaData.email,
                    telefone: empresaData.telefone || '',
                    cpf_cnpj: empresaData.cpf_cnpj || '',
                    endereco: empresaData.endereco || '',
                    responsavel: empresaData.responsavel || '',
                    logo_url: empresaData.logo_url || ''
                  });
                }
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => updateMutation.mutate()}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar Alterações
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}
